import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';

export interface ProblemDetailsBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

export class ApiHttpError extends Error {
  constructor(
    public readonly status: EnumStatusResponse,
    message: string,
    public readonly problem?: ProblemDetailsBody,
  ) {
    super(message);
    this.name = 'ApiHttpError';
  }
}

export function isProblemDetailsBody(value: unknown): value is ProblemDetailsBody {
  return typeof value === 'object' && value !== null;
}

export function resolveProblemMessage(problem: ProblemDetailsBody | undefined, fallback: string): string {
  if (!problem) {
    return fallback;
  }

  if (problem.detail) {
    return problem.detail;
  }

  if (problem.message) {
    return problem.message;
  }

  const firstFieldError = problem.errors
    ? Object.values(problem.errors).flat().find((message) => !!message)
    : undefined;

  if (firstFieldError) {
    return firstFieldError;
  }

  if (problem.title) {
    return problem.title;
  }

  return fallback;
}

export function getFriendlyMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Sua sessão expirou ou você não tem permissão.';
    case 403:
      return 'Você não tem permissão para esta ação.';
    case 404:
      return 'Registro não encontrado.';
    case 409:
      return 'Conflito: o registro foi alterado ou já existe.';
    case 0:
      return 'Não foi possível conectar ao servidor. Verifique sua conexão.';
    default:
      if (status >= 500) {
        return 'Serviço indisponível. Tente novamente em instantes.';
      }
      return 'Ocorreu um erro inesperado. Tente novamente.';
  }
}

export function mapHttpStatusToEnum(status: number): EnumStatusResponse {
  switch (status) {
    case EnumStatusResponse.Success:
    case EnumStatusResponse.Created:
    case EnumStatusResponse.NoContent:
    case EnumStatusResponse.BadRequest:
    case EnumStatusResponse.AccessDenied:
    case EnumStatusResponse.NotFound:
    case EnumStatusResponse.BadGateway:
    case EnumStatusResponse.Unavailable:
      return status;
    default:
      return EnumStatusResponse.UnknownError;
  }
}
